import { Col, Form, InputNumber, Row } from 'antd'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import PerText from '../../../components/custom_components/Text'
import { ScoreGoalDto } from '../../../types'
import { isDefined } from '../../../types.helper'
import { AvatarData } from '../appraisal-flow/shared/appraisal-types'
import { AppraisalContextType } from '../AppraisalTypes'
import { ScoreGridValueBox } from './ScoreGridValueBox'

interface Props {
  score: ScoreGoalDto
  type: AppraisalContextType
  reviewerAvatar: AvatarData
  employeeAvatar: AvatarData
  focus?: boolean
  onBlur: (val: number) => void
  onSetScoreButtonClicked?: (value: number | undefined) => void
  invalid?: boolean
}

export const ScoreGridEntry: FC<Props> = ({
  score,
  type,
  reviewerAvatar,
  employeeAvatar,
  onBlur,
  onSetScoreButtonClicked,
  invalid,
  focus,
}) => {
  const [t] = useTranslation()
  let initValue: number | undefined = undefined // PerInputNumber must be numeric!
  if (type === 'employee') {
    initValue = score.employeeEvaluation ? score.employeeEvaluation : undefined
  } else if (type === 'manager') {
    initValue = score.reviewerEvaluation ? score.reviewerEvaluation : undefined
  } else if (type === 'meeting') {
    initValue = score.resultEvaluation ? score.resultEvaluation : undefined
  }

  let showEmployeeValues = false
  if (type === 'manager') {
    showEmployeeValues = isDefined(score.employeeEvaluation)
  } else if (type === 'meeting') {
    showEmployeeValues = isDefined(score.employeeEvaluation)
  }

  const showManagerValues = type === 'meeting' && isDefined(score.reviewerEvaluation)
  const showLastYearValues = isDefined(score.lastResultEvaluation)

  // dirty fix to prevent the server to receive "" as float
  const onInputChanged = (input: number) => {
    if (typeof input === 'number') {
      const roundedNumber = Math.round(input)
      onBlur(roundedNumber)
    }
  }

  const getScoreGridValueBoxSpan = () => {
    switch (Number(showLastYearValues) + Number(showEmployeeValues) + Number(showManagerValues)) {
      case 1:
        return 24
      case 2:
        return 12
      case 3:
        return 8
      default:
        return 0
    }
  }

  const renderName = (name: AvatarData, type: 'lastName' | 'firstName') => {
    if (type === 'firstName') {
      return name.chosenFirstName ? name.chosenFirstName : name.legalFirstName
    } else if (type === 'lastName') {
      return name.chosenLastName ? name.chosenLastName : name.legalLastName
    }
    return ''
  }

  return (
    <PerFlexRow justify="space-between" align="middle" mb="l" data-e2e-score-goal>
      <Col span={8}>
        <PerText appearance="subheading" mb="xs">
          {t(`appraisalMeeting.evaluation.grid.headline.${score.viewId}`)}
        </PerText>
        <PerText appearance="flowing-text">
          {t(`appraisalMeeting.evaluation.grid.description.${type === 'employee' ? 'employee' : 'manager'}.${score.viewId}`)}
        </PerText>
      </Col>
      <Col span={8}>
        <PerFlexRow justify="center" align="middle">
          <PerText mr="s">{t('appraisalMeeting.evaluation.evaluationValue')}</PerText>
          <Form.Item validateStatus={invalid ? 'error' : ''} style={{ margin: 0 }}>
            <InputNumber
              data-e2e-input-number
              autoFocus={focus}
              min={1}
              max={10}
              value={initValue}
              onChange={(val: number | undefined) => val !== undefined && onInputChanged(val)}
              style={{ width: '75px' }}
            />
          </Form.Item>
        </PerFlexRow>
      </Col>
      <Col span={8}>
        <Row>
          {showLastYearValues && (
            <Col span={getScoreGridValueBoxSpan()}>
              <ScoreGridValueBox
                firstName={t('appraisalMeeting.evaluation.pastYear')}
                value={score.lastResultEvaluation ? score.lastResultEvaluation : undefined}
                onSetScoreButtonClicked={(value) => onSetScoreButtonClicked && onSetScoreButtonClicked(value)}
              />
            </Col>
          )}
          {showEmployeeValues && (
            <Col span={getScoreGridValueBoxSpan()}>
              <ScoreGridValueBox
                nameType={type === AppraisalContextType.Employee ? 'you' : 'firstName'}
                firstName={renderName(employeeAvatar, 'firstName')}
                lastName={renderName(employeeAvatar, 'lastName')}
                value={score.employeeEvaluation ? score.employeeEvaluation : undefined}
                avatarData={employeeAvatar}
                onSetScoreButtonClicked={(value) => onSetScoreButtonClicked && onSetScoreButtonClicked(value)}
              />
            </Col>
          )}
          {showManagerValues && (
            <Col span={getScoreGridValueBoxSpan()}>
              <ScoreGridValueBox
                nameType={[AppraisalContextType.Meeting, AppraisalContextType.Manager].includes(type) ? 'you' : 'firstName'}
                firstName={reviewerAvatar.legalFirstName}
                lastName={reviewerAvatar.legalLastName}
                value={score.reviewerEvaluation ? score.reviewerEvaluation : undefined}
                avatarData={reviewerAvatar}
                onSetScoreButtonClicked={(value) => onSetScoreButtonClicked && onSetScoreButtonClicked(value)}
              />
            </Col>
          )}
        </Row>
      </Col>
    </PerFlexRow>
  )
}
